import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { t } from 'i18next';
import { AutoCompleteSelectEvent } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { DropdownChangeEvent } from 'primereact/dropdown';
import { InputNumber, InputNumberChangeEvent } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Panel, PanelToggleEvent } from 'primereact/panel';
import { SelectButton } from 'primereact/selectbutton';
import { useSearchParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { DonorSchedulingActions } from '@/app/slices/donorScheduling';
import { addToast } from '@/app/slices/toast';
import { AccountAutoComplete, StringMapDropdown } from '@/components';
import {
  SemenAutoComplete,
  SireAutoComplete,
  StorageLocationAutoComplete,
} from '@/components';
import { AdvancedSemenLookup } from '@/components/common/advanced-semen-lookup';
import { AdvancedSireLookup } from '@/components/common/advanced-sire-lookup';
import {
  StringMapAttributeName,
  StringMapObjectTypeCode,
} from '@/components/common/string-map-dropdown';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import {
  LOAD_DONOR_PARTNERS_OPTION_VALUES,
  SEMEN_TYPE_OPTION_VALUES,
} from '@/enums';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useFormNav } from '@/hooks/useFormNav';
import { SEMEN_TYPE_OPTIONS } from '@/pages/donor-scheduling-partnership/constants';
import { DonorSchedule, Semen, Sire } from '@/rtk-query.gql';
import { useSireQuery } from '@/services/sire';
import { appendRevisionComment } from '@/utils/formatting';
import { isSireInactive } from '@/utils/sire';
import { normalizeSubFamily } from '@/utils/subfamilyValues';

import { PartnershipForm } from '../../partnership-form';
import {
  PartnershipTypeLabel,
  PartnershipTypeValue,
} from '../../partnership-form/constants';
import {
  TRUE_FALSE_SELECT_OPTIONS,
  ZERO_ONE_SELECT_OPTIONS,
} from '../constants';

type DonorScheduleFormSemenProps = {
  className?: string;
  collapsed: boolean;
  viewOnly?: boolean;
  onCollapseChange: (collapsed: boolean) => void;
  revisionComment: string;
  setRevisionComment: (revisionComment: string) => void;
  revisionNumber: {
    previousRevision: string;
    currentRevision: string;
  };
  setRevisionNumber: (revisionNumber: {
    previousRevision: string;
    currentRevision: string;
  }) => void;
};

type FormUpdate = Partial<DonorSchedule>;

export const DonorScheduleFormSemen = ({
  className = '',
  collapsed,
  viewOnly = false,
  onCollapseChange,
  revisionComment,
  setRevisionComment,
  revisionNumber,
  setRevisionNumber,
}: DonorScheduleFormSemenProps) => {
  const ref = useRef(null);
  const [params] = useSearchParams();
  const isBackupSemenSireSearchEnabled = useFeatureFlag(
    'DS_backup_semen_sire_search',
  );
  const isClearSireSemenLocationEnabled = useFeatureFlag(
    'Clear_Sire_Semen_Location',
  );
  const isPwpSkipCreditRebillEnabled = useFeatureFlag(
    'Donor_Scheduling_Pwp_Skip_Credit_Rebill_Rule',
  );
  const isPreventDonorSireMismatchEnabled = useFeatureFlag(
    'Prevent_Donor_Schedule_Save_When_Donor_Sire_Do_Not_Match',
  );
  const isSemenSelectionPreservesTtEnabled = useFeatureFlag(
    'DS_Semen_Preserves_TT',
  );
  const isBlockInactiveSiresEnabled = useFeatureFlag('block_inactive_sires');
  const isRevisionMode = params.get('revision') === 'true';

  const {
    ref: formNavRef,
    onKeyDownCapture,
    selectOnFocus: _selectOnFocus,
    handleEnterKeyAdvance,
  } = useFormNav<HTMLDivElement>({
    selectors: [
      'input:not([type="hidden"]):not([disabled]):not([readonly])',
      'select:not([disabled])',
      '.p-dropdown:not(.p-disabled) input',
      '.p-dropdown:not(.p-disabled) input[role="combobox"]',
      '.p-dropdown:not(.p-disabled) .p-dropdown-label',
      '.p-autocomplete:not(.p-disabled) input',
      '.p-calendar:not(.p-disabled) input',
      '.p-inputmask:not(.p-disabled) input',
      '.p-inputnumber:not(.p-disabled) input',
      '.p-selectbutton:not(.p-disabled) .p-button:first-child',
      '.p-nucleus:not(.p-disabled) .p-button:first-child',
      'textarea:not([disabled]):not([readonly])',
      'button:not([disabled])[tabindex]',
    ],
    wrap: true,
    sortByTabIndex: true,
    allowDropdownEnterSelect: true,
  });

  const dispatch = useAppDispatch();
  const { workingDonorSchedule } = useAppSelector(
    (state) => state.donorScheduling,
  );

  const donorSpecies = normalizeSubFamily(
    workingDonorSchedule?.donor?.breed?.subFamily,
  );
  const sireSpecies = normalizeSubFamily(
    workingDonorSchedule?.sire?.breed?.subFamily,
  );
  const hasSpeciesMismatch =
    isPreventDonorSireMismatchEnabled &&
    !!donorSpecies &&
    !!sireSpecies &&
    donorSpecies !== sireSpecies;

  const isDateGreaterThanOrEqualToToday = useMemo(() => {
    return (
      new Date(workingDonorSchedule?.date) >=
      new Date(new Date().setHours(0, 0, 0, 0))
    );
  }, [workingDonorSchedule?.date]);

  const { data: scheduleSire } = useSireQuery(
    workingDonorSchedule?.sireId ?? undefined,
    isBlockInactiveSiresEnabled,
  );
  const warnedInactiveSireScheduleId = useRef<string | null>(null);
  useEffect(() => {
    const scheduleId = workingDonorSchedule?.id;
    if (
      !scheduleId ||
      scheduleSire?.id?.toLowerCase() !==
        workingDonorSchedule?.sireId?.toLowerCase() ||
      warnedInactiveSireScheduleId.current === scheduleId ||
      !isSireInactive(scheduleSire)
    ) {
      return;
    }

    warnedInactiveSireScheduleId.current = scheduleId;
    dispatch(
      addToast({
        severity: 'warn',
        summary: t('sire.inactiveSireTitle'),
        detail: t('sire.inactiveSireOnRecord', {
          name: scheduleSire?.name,
        }),
        life: ToastLife.LONG,
      }),
    );
  }, [
    dispatch,
    scheduleSire,
    workingDonorSchedule?.id,
    workingDonorSchedule?.sireId,
  ]);

  const [backupSemenInputValue, setBackupSemenInputValue] = useState(
    workingDonorSchedule?.backupSemen,
  );

  const updateSchedule = useCallback(
    (updates: FormUpdate) => {
      dispatch({
        type: DonorSchedulingActions.UPDATE_WORKING_DONOR_SCHEDULE,
        payload: {
          ...workingDonorSchedule,
          ...updates,
        } as DonorSchedule,
      });
    },
    [workingDonorSchedule, dispatch],
  );

  useEffect(() => {
    if (
      !workingDonorSchedule?.colCode &&
      workingDonorSchedule?.semen?.colCode
    ) {
      updateSchedule({
        colCode: workingDonorSchedule?.semen?.colCode,
      });
    }
    if (!workingDonorSchedule?.naab && workingDonorSchedule?.semen?.naab) {
      updateSchedule({
        naab: workingDonorSchedule?.semen?.naab,
      });
    }
    if (!workingDonorSchedule?.cane && workingDonorSchedule?.semen?.cane) {
      updateSchedule({
        cane: workingDonorSchedule?.semen?.cane,
      });
    }
  }, [
    workingDonorSchedule?.semen,
    workingDonorSchedule?.colCode,
    workingDonorSchedule?.naab,
    updateSchedule,
    workingDonorSchedule?.cane,
  ]);

  const calculateSemenType = useCallback((semen: Semen) => {
    if (semen.sexed === false) {
      return null;
    }
    if (semen.sex === 1) return SEMEN_TYPE_OPTION_VALUES.SF_F;
    if (semen.sex === 2) return SEMEN_TYPE_OPTION_VALUES.SF_M;
    return null;
  }, []);

  const handleSemenChange = (semen: Semen, semenRevision?: string) => {
    if (isBlockInactiveSiresEnabled && isSireInactive(semen?.sire)) {
      dispatch(
        addToast({
          severity: 'error',
          summary: t('sire.inactiveSireTitle'),
          detail: t('sire.inactiveSireOnSemen', { name: semen?.sire?.name }),
          life: ToastLife.LONG,
        }),
      );
      return false;
    }

    if (isRevisionMode && workingDonorSchedule?.sire?.id !== semen?.sire?.id) {
      const oldSireName = workingDonorSchedule?.sire?.name || 'No Sire';
      const newSireName = semen?.sire?.name || 'No Sire';
      const newNoteText = `Change from Sire, ${oldSireName} to Sire, ${newSireName}. `;

      setRevisionComment(appendRevisionComment(revisionComment, newNoteText));
      dispatch(
        addToast({
          severity: 'info',
          summary: 'Revision Field Changed',
          detail: 'Please update the revision number in the Notes section.',
          life: ToastLife.MEDIUM,
        }),
      );
    }

    if (semenRevision !== undefined && semenRevision !== '') {
      setRevisionNumber({
        previousRevision: revisionNumber.previousRevision,
        currentRevision: semenRevision,
      });
    }

    updateSchedule({
      semen: semen,
      semenId: semen?.id,
      semenLocation: semen?.storageLocation,
      semenLocationId: semen?.storageLocationId,
      semenOwner: semen?.owner,
      semenOwnerId: semen?.ownerId,
      semenPrice: semen?.cost,
      semenType: calculateSemenType(semen),
      sire: semen?.sire,
      sireId: semen?.sire?.id,
      naab: semen?.naab,
      ...(isSemenSelectionPreservesTtEnabled
        ? {}
        : { tt: semen?.export !== null }),
      //cane: semen?.sire?.cane,
      cane: semen?.cane,
      colCode: semen?.colCode,
      creditRebill:
        isPwpSkipCreditRebillEnabled &&
        workingDonorSchedule.owner?.isPwp === true
          ? false
          : workingDonorSchedule.ownerId !== semen?.ownerId,
      // backupSemen: semen?.backupSemen,
      // backupSemenId: semen?.backupSemen?.id,
      // backupSire: semen?.backupSire,
      // backupSireId: semen?.backupSire?.id,
      // backupSemenType: semen?.backupSemenType,
      ...(semenRevision !== undefined && semenRevision !== ''
        ? { revision: semenRevision }
        : {}),
    });
  };

  const clearSemen = useCallback(() => {
    updateSchedule({
      semen: null,
      semenId: null,
      semenLocation: null,
      semenLocationId: null,
      semenOwner: null,
      semenOwnerId: null,
      semenPrice: null,
      semenType: null,
      sire: null,
      sireId: null,
      naab: '',
      ...(isSemenSelectionPreservesTtEnabled ? {} : { tt: false }),
      cane: '',
      colCode: '',
      creditRebill: false,
    });
  }, [updateSchedule, isSemenSelectionPreservesTtEnabled]);

  const clearSire = useCallback(() => {
    if (isRevisionMode) {
      const newNoteText = `Change from Sire, ${workingDonorSchedule?.sire?.name} to Sire, No Sire. `;
      setRevisionComment(appendRevisionComment(revisionComment, newNoteText));
      dispatch(
        addToast({
          severity: 'info',
          summary: 'Revision Field Changed',
          detail: 'Please update the revision number in the Notes section.',
          life: ToastLife.MEDIUM,
        }),
      );
    }
    updateSchedule({ sire: null, sireId: null });
  }, [
    isRevisionMode,
    workingDonorSchedule?.sire?.name,
    revisionComment,
    setRevisionComment,
    dispatch,
    updateSchedule,
  ]);

  const clearSemenLocation = useCallback(() => {
    updateSchedule({ semenLocation: null, semenLocationId: null });
  }, [updateSchedule]);

  const handleBackupSemenSelect = (e: AutoCompleteSelectEvent) => {
    updateSchedule({
      backupSemen: e.value,
      backupSemenId: e.value?.id,
      backupSire: e.value?.sire,
      backupSireId: e.value?.sire?.id,
      backupSemenType: e.value?.semenType,
    });
  };

  const handleBackupSemenChange = (semen: Semen) => {
    updateSchedule({
      backupSemen: semen,
      backupSemenId: semen?.id,
      backupSire: semen?.sire,
      backupSireId: semen?.sire?.id,
      backupSemenType: semen?.semenType,
    });
  };

  const handleBackupSireChange = (sire: Sire) => {
    updateSchedule({
      backupSire: sire,
      backupSireId: sire?.id,
    });
  };

  const clearBackupSemen = useCallback(() => {
    updateSchedule({
      backupSemen: null,
      backupSemenId: null,
      backupSire: null,
      backupSireId: null,
      backupSemenType: null,
    });
  }, [updateSchedule]);

  const clearBackupSire = useCallback(() => {
    updateSchedule({ backupSire: null, backupSireId: null });
  }, [updateSchedule]);

  const handleSireChange = async (sire: Sire) => {
    if (params.get('revision') === 'true') {
      const newNoteText = `Change from Sire, ${workingDonorSchedule?.sire?.name} to Sire, ${sire?.name}. `;

      setRevisionComment(appendRevisionComment(revisionComment, newNoteText));
      dispatch(
        addToast({
          severity: 'info',
          summary: 'Revision Field Changed',
          detail: 'Please update the revision number in the Notes section.',
          life: ToastLife.MEDIUM,
        }),
      );
    }
    updateSchedule({
      sire: sire,
      sireId: sire?.id,
    });
  };
  const getCollapsedValue = () => {
    return workingDonorSchedule?.loadDonorPartners !==
      LOAD_DONOR_PARTNERS_OPTION_VALUES.YES
      ? true
      : isDateGreaterThanOrEqualToToday
        ? true
        : collapsed;
  };

  return (
    <div
      className={`donor-schedule-form-semen ${className}`.trim()}
      onKeyDownCapture={(e) => {
        if (e.key === 'Enter') {
          handleEnterKeyAdvance(e);
        } else {
          onKeyDownCapture(e);
        }
      }}
      ref={formNavRef}
    >
      <Panel
        className="column-span"
        data-pr-disabled={!workingDonorSchedule?.donor}
        ref={ref}
        header={`Partnership - ${workingDonorSchedule?.donor ? workingDonorSchedule?.donor?.name : 'Select a Donor'}`}
        toggleable
        collapsed={getCollapsedValue()}
        onToggle={(e: PanelToggleEvent) => onCollapseChange(e.value)}
      >
        <PartnershipForm.Root>
          <PartnershipForm.Tabs
            types={[
              {
                attributeValue: PartnershipTypeValue.DONOR_PARTNERSHIP_BILLING,
                value:
                  PartnershipTypeLabel[
                    PartnershipTypeValue.DONOR_PARTNERSHIP_BILLING
                  ],
              },
              {
                attributeValue: PartnershipTypeValue.SPECIAL_BILLING,
                value:
                  PartnershipTypeLabel[PartnershipTypeValue.SPECIAL_BILLING],
              },
            ]}
            viewOnly={viewOnly}
          />
        </PartnershipForm.Root>
      </Panel>

      <div className="grid w-full grid-cols-2 gap-6">
        <div className="label-input-group">
          <div className="flex items-end gap-2">
            <AdvancedSemenLookup
              className="general-semen-lookup w-full"
              label={t('semen.semen')}
              value={
                workingDonorSchedule?.semen?.id
                  ? workingDonorSchedule?.semen
                  : null
              }
              ownerId={workingDonorSchedule?.ownerId}
              fertSiteId={workingDonorSchedule?.fertilizationSiteId}
              exportId={workingDonorSchedule?.export}
              onChange={handleSemenChange}
              tabIndex={1}
              disabled={viewOnly}
            />
            <Button
              type="button"
              icon="pi pi-times"
              severity="secondary"
              text
              disabled={viewOnly || !workingDonorSchedule?.semen?.id}
              tabIndex={-1}
              onClick={clearSemen}
            />
          </div>
        </div>
        <div className="label-input-group">
          <div className="flex items-end gap-2">
            <StorageLocationAutoComplete
              label="Semen Location"
              defaultValue={workingDonorSchedule?.semenLocation || undefined}
              field="name"
              disabled={viewOnly}
              onSelect={(e: AutoCompleteSelectEvent) => {
                if (params.get('revision') === 'true') {
                  const newNoteText = `Change from ${workingDonorSchedule?.semenLocation?.name} to ${e.value?.name}. `;
                  setRevisionComment(
                    appendRevisionComment(revisionComment, newNoteText),
                  );
                  dispatch(
                    addToast({
                      severity: 'info',
                      summary: 'Revision Field Changed',
                      detail:
                        'Please update the revision number in the Notes section.',
                      life: ToastLife.MEDIUM,
                    }),
                  );
                }

                updateSchedule({
                  semenLocation: e.value,
                  semenLocationId: e.value?.id,
                });
              }}
              className={`flex-1 ${isRevisionMode ? 'border-yellow-500' : ''}`}
              tooltip={
                isRevisionMode
                  ? 'Changing this field will update revision comment'
                  : ''
              }
              tabIndex={2}
            />
            {isClearSireSemenLocationEnabled && (
              <Button
                type="button"
                icon="pi pi-times"
                severity="secondary"
                text
                disabled={viewOnly || !workingDonorSchedule?.sire?.id}
                tabIndex={-1}
                onClick={clearSemenLocation}
              />
            )}
          </div>
        </div>
        <div className="label-input-group">
          <label>Cane Code</label>
          <div className="flex items-center gap-2">
            <InputText
              value={workingDonorSchedule?.cane ?? ''}
              disabled={viewOnly}
              onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateSchedule({
                  cane: e.target.value,
                })
              }
              tabIndex={3}
              className="w-full"
            />
            <Button
              type="button"
              icon="pi pi-times"
              onClick={() => updateSchedule({ cane: '' })}
              disabled={viewOnly}
              tabIndex={-1}
              severity="secondary"
              text
            />
          </div>
        </div>
        <div className="label-input-group">
          <AccountAutoComplete
            label="Semen Owner"
            defaultValue={workingDonorSchedule?.semenOwner || undefined}
            field="name"
            disabled={viewOnly}
            onSelect={(e: AutoCompleteSelectEvent) => {
              if (params.get('revision') === 'true') {
                const newNoteText = `Change from ${workingDonorSchedule?.semenOwner?.code} to ${e.value?.code}. `;
                setRevisionComment(
                  appendRevisionComment(revisionComment, newNoteText),
                );
                dispatch(
                  addToast({
                    severity: 'info',
                    summary: 'Revision Field Changed',
                    detail:
                      'Please update the revision number in the Notes section.',
                    life: ToastLife.MEDIUM,
                  }),
                );
              }

              updateSchedule({
                semenOwner: e.value,
                semenOwnerId: e.value?.id,
              });
            }}
            className={isRevisionMode ? 'border-yellow-500' : ''}
            tooltip={
              isRevisionMode
                ? 'Changing this field will update revision comment'
                : ''
            }
            tabIndex={4}
          />
        </div>

        <div className="label-input-group">
          <label>Collection Code</label>
          <div className="flex items-center gap-2">
            <InputText
              value={workingDonorSchedule?.colCode ?? ''}
              disabled={viewOnly}
              onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (
                  params.get('revision') === 'true' &&
                  e.target.value?.length >= 5
                ) {
                  const newNoteText = `Change in Collection Code. `;
                  setRevisionComment(
                    appendRevisionComment(revisionComment, newNoteText),
                  );
                  dispatch(
                    addToast({
                      severity: 'info',
                      summary: 'Revision Field Changed',
                      detail:
                        'Please update the revision number in the Notes section.',
                      life: ToastLife.MEDIUM,
                    }),
                  );
                }
                updateSchedule({
                  colCode: e.target.value,
                });
              }}
              className={`w-full ${isRevisionMode ? 'border-yellow-500' : ''}`}
              tooltip={
                isRevisionMode
                  ? 'Changing this field will update revision comment'
                  : ''
              }
              tabIndex={5}
            />
            <Button
              type="button"
              icon="pi pi-times"
              onClick={() => updateSchedule({ colCode: '' })}
              disabled={viewOnly}
              tabIndex={-1}
              severity="secondary"
              text
            />
          </div>
        </div>
        <div className="label-input-group">
          {isBackupSemenSireSearchEnabled ? (
            <div className="flex items-end gap-2">
              <AdvancedSemenLookup
                className="general-semen-lookup w-full"
                label="Backup Semen"
                value={
                  workingDonorSchedule?.backupSemen?.id
                    ? workingDonorSchedule?.backupSemen
                    : null
                }
                ownerId={workingDonorSchedule?.ownerId}
                fertSiteId={workingDonorSchedule?.fertilizationSiteId}
                exportId={workingDonorSchedule?.export}
                onChange={handleBackupSemenChange}
                tabIndex={6}
                disabled={viewOnly}
              />
              <Button
                type="button"
                icon="pi pi-times"
                severity="secondary"
                text
                disabled={viewOnly || !workingDonorSchedule?.backupSemen?.id}
                tabIndex={-1}
                onClick={clearBackupSemen}
              />
            </div>
          ) : (
            <SemenAutoComplete
              label="Backup Semen"
              defaultValue={workingDonorSchedule?.backupSemen || undefined}
              field="name"
              disabled={viewOnly}
              onSelect={handleBackupSemenSelect}
              inputValue={backupSemenInputValue}
              setInputValue={setBackupSemenInputValue}
              tabIndex={6}
            />
          )}
        </div>

        <div className="label-input-group">
          <label>NAAB</label>
          <div className="flex items-center gap-2">
            <InputText
              value={workingDonorSchedule?.naab ?? ''}
              disabled={viewOnly}
              onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateSchedule({
                  naab: e.target.value,
                })
              }
              tabIndex={7}
              className="w-full"
            />
            <Button
              type="button"
              icon="pi pi-times"
              onClick={() => updateSchedule({ naab: '' })}
              disabled={viewOnly}
              tabIndex={-1}
              severity="secondary"
              text
            />
          </div>
        </div>
        <div className="label-input-group">
          {isBackupSemenSireSearchEnabled ? (
            <AdvancedSireLookup
              label="Backup Sire"
              value={
                workingDonorSchedule?.backupSire?.id
                  ? workingDonorSchedule?.backupSire
                  : null
              }
              ownerId={workingDonorSchedule?.owner?.id}
              onChange={handleBackupSireChange}
              onClear={clearBackupSire}
              tabIndex={8}
              disabled={viewOnly}
            />
          ) : (
            <SireAutoComplete
              label="Backup Sire"
              defaultValue={workingDonorSchedule?.backupSire || undefined}
              field="name"
              disabled={viewOnly}
              onSelect={(e: AutoCompleteSelectEvent) =>
                updateSchedule({
                  backupSire: e.value,
                  backupSireId: e.value?.id,
                })
              }
              tabIndex={8}
            />
          )}
        </div>

        <div className="label-input-group">
          <AdvancedSireLookup
            className={`${isRevisionMode ? 'border-yellow-500' : ''}${hasSpeciesMismatch ? 'p-invalid' : ''}`}
            label={t('sire.sire')}
            value={
              workingDonorSchedule?.sire?.id ? workingDonorSchedule?.sire : null
            }
            ownerId={workingDonorSchedule?.owner?.id}
            onChange={handleSireChange}
            onClear={isClearSireSemenLocationEnabled ? clearSire : undefined}
            revisionComment={revisionComment}
            setRevisionComment={setRevisionComment}
            revisionNumber={revisionNumber}
            setRevisionNumber={setRevisionNumber}
            tooltip={
              isRevisionMode
                ? 'Changing this field will update revision comment'
                : ''
            }
            tabIndex={9}
            disabled={viewOnly}
          />
          {hasSpeciesMismatch && (
            <small className="p-error">
              {t('donorScheduling.speciesMismatchDetail', {
                defaultValue:
                  'The selected sire must match the donor species. Please select a sire with the same species as the donor.',
              })}
            </small>
          )}
        </div>
        <div className="label-input-group">
          <StringMapDropdown
            label="Backup Semen Type"
            value={workingDonorSchedule?.backupSemenType || undefined}
            objectTypeCode={StringMapObjectTypeCode.SEMEN_TYPE}
            attributeName={StringMapAttributeName.SEMEN_TYPE}
            disabled={viewOnly}
            onChange={(e: DropdownChangeEvent) =>
              updateSchedule({
                backupSemenType: e.value,
              })
            }
            tabIndex={10}
          />
        </div>

        <div className="label-input-group">
          <StringMapDropdown
            label="Semen Type"
            value={workingDonorSchedule?.semenType || undefined}
            objectTypeCode={StringMapObjectTypeCode.SEMEN_TYPE}
            attributeName={StringMapAttributeName.SEMEN_TYPE}
            disabled={viewOnly}
            onChange={(e: DropdownChangeEvent) => {
              const semenType = SEMEN_TYPE_OPTIONS.find(
                (option) => option.value === e.value,
              );
              if (params.get('revision') === 'true') {
                const newNoteText = `Change from ${workingDonorSchedule?.semenTypeValue} to ${semenType?.name}. `;
                setRevisionComment(
                  appendRevisionComment(revisionComment, newNoteText),
                );
                dispatch(
                  addToast({
                    severity: 'info',
                    summary: 'Revision Field Changed',
                    detail:
                      'Please update the revision number in the Notes section.',
                    life: ToastLife.MEDIUM,
                  }),
                );
              }

              updateSchedule({
                semenType: e.value,
              });
            }}
            className={isRevisionMode ? 'border-yellow-500' : ''}
            tooltip={
              isRevisionMode
                ? 'Changing this field will update revision comment'
                : ''
            }
            tabIndex={11}
          />
        </div>
        <div className="label-input-group">
          <label>Contract Semen</label>
          <SelectButton
            className="p-nucleus"
            value={workingDonorSchedule?.contractSemen}
            disabled={viewOnly}
            onChange={(e: DropdownChangeEvent) =>
              updateSchedule({
                contractSemen: e.value,
              })
            }
            options={TRUE_FALSE_SELECT_OPTIONS}
            optionLabel="name"
            optionValue="value"
            tabIndex={12}
          />
        </div>

        <div className="label-input-group">
          <label>Separate Fert.</label>
          <SelectButton
            className="p-nucleus"
            value={workingDonorSchedule?.separateFert}
            disabled={viewOnly}
            onChange={(e: DropdownChangeEvent) =>
              updateSchedule({
                separateFert: e.value,
              })
            }
            options={TRUE_FALSE_SELECT_OPTIONS}
            optionLabel="name"
            optionValue="value"
            tabIndex={13}
          />
        </div>
        <div className="label-input-group">
          <label>Semen Released</label>
          <SelectButton
            className="p-nucleus"
            value={workingDonorSchedule?.semenReleased}
            disabled={viewOnly}
            onChange={(e: DropdownChangeEvent) =>
              updateSchedule({
                semenReleased: e.value,
              })
            }
            options={TRUE_FALSE_SELECT_OPTIONS}
            optionLabel="name"
            optionValue="value"
            tabIndex={14}
          />
        </div>

        <div className="label-input-group">
          <label>Expected Semen Qty</label>
          <InputText
            value={
              workingDonorSchedule?.expectedSemenQty?.toString() || undefined
            }
            disabled={viewOnly}
            onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
              updateSchedule({
                expectedSemenQty: Number(e.target.value),
              })
            }
            tabIndex={15}
          />
        </div>
        <div className="label-input-group">
          <label>Critical</label>
          <SelectButton
            className="p-nucleus"
            value={workingDonorSchedule?.critical}
            disabled={viewOnly}
            onChange={(e: DropdownChangeEvent) =>
              updateSchedule({
                critical: e.value ? 1 : 0,
              })
            }
            options={ZERO_ONE_SELECT_OPTIONS}
            optionLabel="name"
            optionValue="value"
            tabIndex={16}
          />
        </div>

        <div className="label-input-group">
          <label>Credit/Rebill</label>
          <SelectButton
            className="p-nucleus"
            value={workingDonorSchedule?.creditRebill}
            disabled={viewOnly}
            onChange={(e: DropdownChangeEvent) =>
              updateSchedule({
                creditRebill: e.value,
              })
            }
            allowEmpty={false}
            options={TRUE_FALSE_SELECT_OPTIONS}
            optionLabel="name"
            optionValue="value"
            tabIndex={17}
          />
        </div>
        <div className="label-input-group">
          <label>{t('semen.secondaryOwner')}</label>
          <InputText
            value={workingDonorSchedule?.semen?.secondaryOwner ?? ''}
            disabled
          />
        </div>

        <div className="label-input-group">
          <label>Semen Price</label>
          <InputNumber
            value={workingDonorSchedule?.semenPrice ?? undefined}
            mode="currency"
            currency="USD"
            locale="en-US"
            disabled={viewOnly}
            onChange={(e: InputNumberChangeEvent) =>
              updateSchedule({
                semenPrice: e.value,
              })
            }
            tabIndex={18}
          />
        </div>
        <div />

        <div className="label-input-group">
          <label>Semen Comment</label>
          <InputText
            value={workingDonorSchedule?.semenComment || ''}
            disabled={viewOnly}
            onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
              updateSchedule({
                semenComment: e.target.value,
              })
            }
            tabIndex={19}
          />
        </div>
      </div>
    </div>
  );
};
